import type * as React from 'react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import dayjs from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import {
  Lightbox,
  ClockIcon,
  Badge,
  Tooltip,
  IgReelIcon,
  CarouselIcon,
  Input,
  toast,
} from '@buffer-mono/popcorn'
import type { LightboxProps } from '@buffer-mono/popcorn'

import { PostCardContextProvider } from '~publish/components/PostCard/PostCardContext'
import { PostMediaLightbox } from '~publish/components/PostCard/PostMedia/PostMediaLightbox'

import type { PostCard_PostData } from '~publish/components/PostCard/PostCard'
import { PostMediaAsset_Asset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { readFragment } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { isValidURL, urlHasProtocol } from '~publish/legacy/grid/util/index'
import { getResizedWebpImageUrlWithWidth } from '~publish/legacy/utils/bufferImages'

import styles from './index.module.css'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

function onLinkSave(
  post: PostCard_PostData,
  newValue: string,
  onSavePostUrl: (post: PostCard_PostData, link: string) => void,
  onValidationError: () => void,
): void {
  if (post.metadata?.__typename !== 'InstagramPostMetadata') {
    return
  }

  const link = post.metadata?.link || ''

  if (link !== newValue) {
    if (newValue === '') {
      onSavePostUrl(post, '')
    } else {
      if (isValidURL(newValue)) {
        onSavePostUrl(
          post,
          urlHasProtocol(newValue) ? newValue : `https://${newValue}`,
        )
      } else {
        onValidationError()
        toast.error('The URL format is invalid!')
      }
    }
  }
}

function ExtraContent({
  post,
  hasWriteAccess,
  onSavePostUrl,
  onValidationError,
  onReset,
  hasError,
}: {
  post: PostCard_PostData
  hasWriteAccess: boolean
  onSavePostUrl: (post: PostCard_PostData, link: string) => void
  onReset: () => void
  onValidationError: () => void
  hasError?: boolean
}): JSX.Element | null {
  if (post.metadata?.__typename !== 'InstagramPostMetadata') {
    return null
  }

  const link = post.metadata?.link || ''

  return (
    <div className={styles.inputWrapper}>
      <Input
        aria-invalid={hasError}
        disabled={!hasWriteAccess}
        onFocus={onReset}
        onBlur={(event): void => {
          onLinkSave(post, event.target.value, onSavePostUrl, onValidationError)
        }}
        onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>): void => {
          if (event.key === 'Enter') {
            onLinkSave(
              post,
              event.currentTarget.value,
              onSavePostUrl,
              onValidationError,
            )
          } else {
            onReset()
          }
        }}
        name="postUrl"
        placeholder="Website or Product URL"
        defaultValue={link || ''}
      />
    </div>
  )
}

const getTooltipText = (
  post: PostCard_PostData,
  timezone?: string,
  isScheduled?: boolean,
): string => {
  const timestamp = isScheduled ? post.dueAt : post.sentAt

  let slotMoment = dayjs(timestamp)

  if (timezone) {
    slotMoment = slotMoment.tz(timezone)
  }

  const humanReadableTime = slotMoment.format('MMM Do YYYY [at] h:mm a')
  const sentText = isScheduled ? 'will be' : 'was'

  return `This post ${sentText} sent ${humanReadableTime}`
}

interface GridListPostProps {
  post: PostCard_PostData
  timezone?: string
  showExtraContent?: boolean
  hasWriteAccess: boolean
  onSavePostUrl: (post: PostCard_PostData, link: string) => void
}

const GridListPost = ({
  post,
  timezone = '',
  showExtraContent,
  hasWriteAccess,
  onSavePostUrl,
}: GridListPostProps): JSX.Element => {
  const [hasError, setHasError] = useState(false)
  const { triggerAttributes, editPostInComposer } = usePostComposer()
  const isScheduled = post.status === 'scheduled'
  const ImageItem = isScheduled ? 'button' : Lightbox.Trigger
  const assets = readFragment(PostMediaAsset_Asset, post.assets)
  const firstAsset = assets[0]
  const image = isOfType(firstAsset, 'VideoAsset')
    ? firstAsset.thumbnail
    : firstAsset.source

  const slides: LightboxProps['slides'] = assets
    .map((asset) => {
      if (isOfType(asset, 'VideoAsset')) {
        return {
          type: 'video' as const,
          poster: asset.thumbnail,
          width: asset.video.width,
          height: asset.video.height,
          sources: [{ src: asset.source, type: asset.mimeType }],
        }
      }
      if (isOfType(asset, 'ImageAsset')) {
        return {
          type: 'image' as const,
          src: asset.source ?? asset.thumbnail,
        }
      }
      return { src: asset.source ?? asset.thumbnail }
    })
    .filter(Boolean)

  useEffect(() => {
    if (!showExtraContent) {
      setHasError(false)
    }
  }, [showExtraContent])

  return (
    <div className={styles.itemStyle}>
      <PostCardContextProvider post={post}>
        <PostMediaLightbox slides={slides}>
          <Tooltip content={getTooltipText(post, timezone, isScheduled)}>
            <ImageItem
              {...triggerAttributes}
              aria-label={isScheduled ? 'Edit post' : 'View'}
              className={styles.imgWrapper}
              style={{
                backgroundImage: `url("${getResizedWebpImageUrlWithWidth(
                  image,
                  820,
                )}")`,
              }}
              onClick={
                isScheduled
                  ? (): void => {
                      editPostInComposer({
                        cta: 'publish-gridList-gridItem-editPost-1',
                        postId: post.id,
                      })
                    }
                  : undefined
              }
            >
              {isScheduled && (
                <div className={styles.scheduledIcon}>
                  <Badge variant="neutral">
                    <ClockIcon size="medium" />
                    Scheduled
                  </Badge>
                </div>
              )}
              {post.assets.length > 1 && (
                <div className={styles.typeIcon}>
                  <CarouselIcon size="medium" />
                </div>
              )}
              {post.metadata?.type === 'reel' && (
                <div className={styles.typeIcon}>
                  <IgReelIcon size="medium" />
                </div>
              )}
            </ImageItem>
          </Tooltip>
        </PostMediaLightbox>
      </PostCardContextProvider>
      {hasError && <div className={styles.error} />}
      {showExtraContent && (
        <ExtraContent
          post={post}
          hasWriteAccess={hasWriteAccess}
          onSavePostUrl={onSavePostUrl}
          onValidationError={(): void => {
            setHasError(true)
          }}
          onReset={(): void => {
            setHasError(false)
          }}
          hasError={hasError}
        />
      )}
    </div>
  )
}

export default GridListPost
